import { useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { TradingAccountCountryDto } from 'api/Trading-Accounts/Types/TradingAccountCountryDto'
import { UpdateTradingAccountCountry } from 'api/Trading-Accounts/Types/UpdateTradingAccountCountry'
import { Modal } from 'components/CustomModal'
import { FilterCell } from 'components/Filter/FilterCell'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { normalizeFilter } from 'features/DataGrid'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import stringComparator from 'utils/stringComparator'

import {
  AdministrationCountriesNavigation,
  AdministrationCountriesTab,
} from '../AdministrationCountriesNavigation'
import EditTradingAccountCountryPage from './EditTradingAccountCountryPage'
import tradingAccountsColumns from './tradingAccountsColumns'

const TradingAccountsCountries = (): JSX.Element => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [countries, setCountries] = useState<TradingAccountCountryDto[]>([])
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<{ skip: number; take: number }>(
    getPaginationInitialState()
  )
  const [tradingAccountCountry, setTraadingAccountCountry] =
    useState<TradingAccountCountryDto | null>(null)
  const [isModalOpen, setModalOpen] = useState<boolean>(false)
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(
    () => tradingAccountsApiClient.getTradingAccountCountries(),
    []
  )
  const { data, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (data) {
      setCountries(data)
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: UpdateTradingAccountCountry) => {
    if (tradingAccountCountry) {
      await tradingAccountsApiClient.updateTradingAccountCountry(
        tradingAccountCountry.tickmillCompany.id,
        tradingAccountCountry.country.id,
        values
      )
      setTraadingAccountCountry(null)
      setModalOpen(false)
      setToast(successToast('Trading Account Country updated.'))
      forceRefresh()
    }
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPaginationState({
      ...getPaginationInitialState(),
      skip: 0,
      take: paginationState.take,
    })
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    if (data) {
      setPaginationState({ skip: event.page.skip, take: event.page.take })
    }
  }

  const handleEdit = (countryPreference: TradingAccountCountryDto) => {
    setTraadingAccountCountry(countryPreference)
    setModalOpen(true)
  }

  if (isLoading) {
    return <Spinner />
  }

  const gridData = filterBy(countries || [], filter).sort(
    (a: TradingAccountCountryDto, b: TradingAccountCountryDto) =>
      stringComparator(a.country.name, b.country.name)
  )

  return (
    <>
      {isModalOpen && tradingAccountCountry && (
        <Modal visible={isModalOpen} onClose={() => setModalOpen(false)}>
          <EditTradingAccountCountryPage data={tradingAccountCountry} onSubmit={onSubmit} />
        </Modal>
      )}
      <Breadcrumbs
        crumbs={[{ title: 'Trading Accounts', link: links.administrationTradingAccounts.url }]}
      />
      <AdministrationCountriesNavigation tab={AdministrationCountriesTab.TRADING_ACCOUNTS} />
      <Grid
        filterable
        data={gridData.slice(paginationState.skip, paginationState.take + paginationState.skip)}
        filter={filter}
        skip={paginationState.skip}
        take={paginationState.take}
        total={gridData.length}
        onPageChange={handlePageChange}
        onFilterChange={handleFilterChange}
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        {tradingAccountsColumns
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx} {...column} />
          ))}
        {hasPermission(permissions, PermissionsEnum.CanEditCountryConfiguration) && (
          <Column
            width={50}
            title=''
            filterCell={FilterCell}
            sortable={false}
            cell={(props: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      'data-test': 'edit',
                      onClick: () => handleEdit(props.dataItem),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        )}
      </Grid>
    </>
  )
}

export default TradingAccountsCountries
